import { Form, FormInstance } from "antd";
import { Fragment, useEffect } from "react";
import FormBox from "../../Components/Form";
import StaticDropdown from "../../Components/Dropdown/StaticDropdown";
import InsWithTypeDropdown from "../../Components/Dropdown/InsWithTypeDropdown";
import TextInput from "../../Components/TextInput";
import SimpleTextInput from "../../Components/SimpleTextInput";
import Button from "../../Components/Button";
import { IStepOptionDropdownItem } from "../../Interfaces/stepInterfaces";
import { KaminInput, KaminOrder } from "../../Requests/kamin2leg";
import { criterionBoundaryFields, criterionFieldLabel, parameterFields } from "./fields";
import { toast } from "react-toastify";
import { linkedNLegVolume } from "../../Utils/nLegVolume";

interface Props {
  form: FormInstance<KaminInput>;
  accounts: { id: number; name: string }[];
  instruments: IStepOptionDropdownItem[];
  editing?: KaminOrder;
  busy: boolean;
  ready: boolean;
  onCancel?: () => void;
  onPreview: (input: KaminInput) => void;
  onChange: () => void;
}

export function NumericField({ value, onChange, label, name, integer, ratio, side, disabled }: {
  value?: number | string; onChange?: (value: string) => void; label: string; name: string;
  integer?: boolean; ratio?: boolean; side?: number; disabled?: boolean;
}) {
  if (ratio) return <SimpleTextInput name={name} value={value?.toString()} disabled={disabled}
    placeholder={label} kind="priceWithDecimals" className={`!max-w-[64px] !w-full text-center pr-0 ${
      side === 1 ? "border-green-700 focus:border-green-700" : side === 2
        ? "border-red-700 focus:border-red-700" : "border-gray-600 focus:border-gray-600"}`}
    onChange={(_, next) => onChange?.(next)} />;
  return <TextInput name={name} label={label} value={value?.toString()} extraLabel disabled={disabled}
    kind={integer ? "number" : "negativeDecimalNumbers"} onChange={(_, next) => onChange?.(next)} />;
}

function DropdownField({ value, onChange, label, options, disabled }: {
  value?: number | string; onChange?: (value: number | string | null) => void; label: string;
  options: { id: number | string; name: string }[]; disabled?: boolean;
}) {
  return <StaticDropdown dropdown={{ name: label, placeholder: label, extraLabel: true, disabled }}
    options={options} value={value} onChange={(_, next) => onChange?.(next)} />;
}

export function normalizeKaminInput(input: KaminInput): KaminInput {
  const { aheadTicks, competitionSeconds, hedgeTimeoutSeconds, maxAheadFloorPercent, aheadFloorInsensitivityPercent, maxImbalancePercent, ...orderInput } = input;
  return { ...orderInput, ...Object.fromEntries(parameterFields.map(field => [field.key, field.optional && (input[field.key] == null || String(input[field.key]).trim() === "") ? null : Number(input[field.key])])),
    criterionType: input.criterionType ?? "SignedCost",
    kaminLegIndex: Number(input.kaminLegIndex),
    legs: input.legs.map(leg => ({ ...leg, ratio: Number(leg.ratio), side: Number(leg.side),
      sahraUserId: Number(leg.sahraUserId), optionOrderType: Number(leg.optionOrderType),
      ...(leg.volume == null ? {} : { volume: Number(leg.volume) }) })) };
}

function legacyLegVolumes(input: KaminInput, instruments: IStepOptionDropdownItem[]) {
  const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
  const ratios = input.legs.map(x => Math.round(Number(x.ratio) * 100));
  const cs = input.legs.map(leg => instruments.find(x => x.id === leg.instrumentId)?.contractSize || 1);
  const quantities = ratios.map((r, i) => r / gcd(ratios[0], ratios[1]) * cs[1 - i] / gcd(cs[0], cs[1]));
  const divisor = gcd(quantities[0], quantities[1]);
  return quantities.map(q => input.totalVolume * q / divisor);
}

export default function KaminOrderForm({ form, accounts, instruments, editing, busy, ready, onCancel, onPreview, onChange }: Props) {
  const legs = Form.useWatch("legs", form) || [];
  const criterionType = Form.useWatch("criterionType", form) ?? "SignedCost";
  const account1 = legs[0]?.sahraUserId;
  const dropdownOptions = { required: true, message: "مقدار لازم است" };
  const contractSize = (index: number, values = legs) => instruments.find(x => x.id === values[index]?.instrumentId)?.contractSize || 1;
  useEffect(() => {
    if (legs.length !== 2 || legs.some(leg => !(Number(leg.ratio) > 0)) || legs.every(leg => leg.volume != null)) return;
    if (legs.some(leg => leg.instrumentId && !instruments.some(x => x.id === leg.instrumentId))) return;
    const volumes = legacyLegVolumes(form.getFieldsValue(true), instruments);
    form.setFieldValue("legs", legs.map((leg, i) => ({ ...leg, volume: volumes[i] })));
  }, [legs, instruments, form]); // Populate old orders from their persisted package quantities.
  const changeVolume = (index: number, value: string, ratioChange = false) => {
    const values = form.getFieldValue("legs") as KaminInput["legs"];
    const next = values.map(leg => ({ ...leg }));
    if (ratioChange) next[index].ratio = Number(value);
    next[index].volume = Number(value);
    next[1 - index].volume = linkedNLegVolume(Number(value), contractSize(index, next), contractSize(1 - index, next),
      Number(next[index].ratio), Number(next[1 - index].ratio));
    form.setFieldValue("legs", next); onChange();
  };
  const finish = (input: KaminInput) => {
    const normalized = normalizeKaminInput(input);
    // An unchanged legacy edit retains its existing request contract and package target.
    if (editing && editing.input.legs.every(leg => leg.volume == null)) {
      const volumes = legacyLegVolumes(editing.input, instruments);
      if (normalized.legs.every((leg, i) => Number(leg.volume) === volumes[i]))
        normalized.legs = normalized.legs.map(({ volume, ...leg }) => leg);
    }
    onPreview(normalized);
  };
  return <FormBox className={`${editing ? "!w-full kamin-edit-form" : "!w-80"} gap-3 p-2 px-2 kamin-order-form`}>
    <Form form={form} component={false} onValuesChange={onChange}
      onFinishFailed={({ errorFields }) => { if (errorFields[0]?.errors[0]) toast.error(errorFields[0].errors[0]); }}
      onFinish={finish}>
      <Form.Item hidden name="totalVolume"><input /></Form.Item>
      {editing && <div className="text-xs font-bold">ویرایش {editing.id}</div>}
      <div className={editing ? "kamin-edit-fields" : "w-full flex flex-col gap-3"}>
      <div className="w-full min-w-0 flex flex-col gap-3">
      <div className="w-full flex flex-col gap-2">
        <StaticDropdown dropdown={{ name: "account", placeholder: "اکانت", extraLabel: true, disabled: busy || !!editing }}
          options={accounts} value={account1} onChange={(_, value) => {
            [0, 1].forEach(i => form.setFieldValue(["legs", i, "sahraUserId"], value)); onChange();
          }} />
        {[0, 1].map(i => <Form.Item hidden key={i}
          name={["legs", i, "sahraUserId"]} rules={[dropdownOptions]}><input /></Form.Item>)}
      </div>
      <div className="flex flex-row gap-1 w-full">{[0, 1].map(i => <Form.Item noStyle key={i}
        name={["legs", i, "side"]} rules={[dropdownOptions]}><DropdownField label={`جهت ${i === 0 ? "اول" : "دوم"}`}
          options={[{ id: 1, name: "خرید" }, { id: 2, name: "فروش" }]} disabled={busy || !!editing} /></Form.Item>)}</div>
      <div className={editing ? "grid grid-cols-2 gap-2 w-full" : "w-full flex flex-col gap-3"}>
      {[0, 1].map(i => <div className="w-full min-w-0" key={i}>
        <Form.Item hidden name={["legs", i, "instrumentId"]} rules={[dropdownOptions]}><input /></Form.Item>
        <Form.Item hidden name={["legs", i, "optionOrderType"]} rules={[dropdownOptions]}><input /></Form.Item>
        <InsWithTypeDropdown insInfo={{ name: "instrumentId", placeholder: `نماد ${i === 0 ? "اول" : "دوم"}`, disabled: busy || !!editing }}
          typeInfo={{ name: "optionOrderType", placeholder: `نوع معامله اختیار ${i === 0 ? "اول" : "دوم"}`, disabled: busy || !!editing }}
          insValue={legs[i]?.instrumentId} typeValue={legs[i]?.optionOrderType} instrumentOptions={instruments} extraLabel
          onInsChange={(_, value) => { form.setFieldValue(["legs", i, "instrumentId"], value);
            form.setFieldValue(["legs", i, "optionOrderType"], 2);
            changeVolume(i, String(form.getFieldValue(["legs", i, "volume"]) ?? 1)); }}
          onTypeChange={(_, value) => { form.setFieldValue(["legs", i, "optionOrderType"], value); onChange(); }} />
      </div>)}
      </div>
      <div className="flex flex-row justify-between items-end gap-2 w-full">
        <div className="text-xs pb-1.5">نسبت اجرا:</div>
        {[0, 1].map(i => <div className="flex flex-col items-center gap-0.5 w-16" key={i}>
          <span className={`text-xs ${legs[i]?.side === 1 ? "text-green-700" : legs[i]?.side === 2 ? "text-red-700" : "text-gray-600"}`}>{i === 0 ? "اول" : "دوم"}</span>
          <Form.Item noStyle name={["legs", i, "ratio"]} rules={[dropdownOptions]}>
            <NumericField label={`نسبت ${i === 0 ? "اول" : "دوم"}`} name={`ratio${i}`} ratio side={legs[i]?.side} disabled={busy || !!editing}
              onChange={value => changeVolume(i, value, true)} />
          </Form.Item>
        </div>)}
      </div>
      <div className={editing ? "grid grid-cols-2 gap-2 w-full" : "w-full flex flex-col gap-3"}>
      <Form.Item noStyle name="kaminLegIndex" rules={[dropdownOptions]}><DropdownField label="لگ کمین"
        options={[{ id: 0, name: "اول" }, { id: 1, name: "دوم" }]} disabled={busy || !!editing} /></Form.Item>
      <StaticDropdown dropdown={{ name: "volumeType", placeholder: "روش تعیین حجم", extraLabel: true, disabled: true }}
        options={[{ id: 1, name: "مقدار ثابت" }]} value={1} onChange={() => {}} />
      </div>
      <div className="grid grid-cols-2 gap-2 w-full">
        {[0, 1].map(i => <Form.Item noStyle key={i} name={["legs", i, "volume"]} rules={[dropdownOptions, {
          validator: (_, value) => Number.isFinite(Number(value)) && Number(value) >= 1 && Number(value) <= 2147483647
            ? Promise.resolve() : Promise.reject(new Error("حجم هر لگ باید حداقل یک و در محدوده مجاز باشد")),
        }]}><NumericField name={`volume${i}`} label={i === 0 ? "حجم پایه" : "حجم دوم"} integer disabled={busy}
          onChange={value => changeVolume(i, value)} /></Form.Item>)}
      </div>
      </div>
      <div className={editing ? "kamin-edit-parameters" : "grid grid-cols-2 gap-x-2 gap-y-3 w-full"}>
        <div className="col-span-2">
          <Form.Item noStyle name="criterionType" initialValue="SignedCost" rules={[dropdownOptions]}>
            <DropdownField label="معیار" options={[{ id: "SignedCost", name: "بهای تمام‌شده" }]} disabled={busy} />
          </Form.Item>
        </div>
        {parameterFields.filter(field => field.key !== "totalVolume" && (!editing || !criterionBoundaryFields.some(boundary => boundary.key === field.key)))
          .map(field => <div key={field.key} className={!editing && field.key === "totalVolume"
          ? "col-span-2" : editing && field.key === "huntMaxCost" ? "col-start-1" : ""}>
          <Form.Item noStyle name={field.key} rules={[{ ...dropdownOptions, required: !field.optional }, {
            validator: (_, value) => {
              if (field.optional && (value == null || value === "")) return Promise.resolve();
              const n = Number(value);
              return Number.isFinite(n) && (!field.integer || Number.isInteger(n)) &&
                (field.min === undefined || n >= field.min) && (field.max === undefined || n <= field.max)
                ? Promise.resolve() : Promise.reject(new Error("مقدار خارج از محدوده مجاز است"));
            },
          }]}><NumericField name={field.key} label={criterionFieldLabel(field.key, field.label, criterionType)} integer={field.integer} disabled={busy} /></Form.Item>
        </div>)}
      </div>
      </div>
      {editing && <div className="kamin-boundary-entry w-full" dir="rtl">
        <div className="text-xs mb-2">مرزهای معیار · {criterionType === "SignedCost" ? "مقدار کمتر مطلوب‌تر است" : "مقدار بیشتر مطلوب‌تر است"}</div>
        <div className="kamin-boundary-inputs">
          {criterionBoundaryFields.map((field, index) => <Fragment key={field.key}>
            {index > 0 && <span className="kamin-boundary-sign">{criterionType === "SignedCost" ? "≥" : "≤"}</span>}
            <div className="kamin-criterion-boundary" style={{ borderTopColor: field.color }}>
              <Form.Item noStyle name={field.key} rules={[dropdownOptions, {
                validator: (_, value) => Number.isFinite(Number(value)) ? Promise.resolve()
                  : Promise.reject(new Error("مقدار معتبر وارد کنید")),
              }]}><NumericField name={field.key} label={criterionFieldLabel(field.key, field.label, criterionType)} disabled={busy} /></Form.Item>
            </div>
          </Fragment>)}
        </div>
      </div>}
      {editing ? <div className="flex flex-row justify-between items-center w-full mt-2">
        <Button text="لغو" onClick={() => onCancel?.()} variant="outlined" className="!w-16 !h-6 !text-xs" disabled={busy} />
        <Button text="ارسال" onClick={() => form.submit()} variant="contained" className="!w-16 !h-6 !text-xs" disabled={!ready || busy} loading={busy} />
      </div> : <Button text="ارسال" onClick={() => form.submit()} variant="contained" disabled={!ready || busy} loading={busy} />}
    </Form>
  </FormBox>;
}
